// Reviewer-search orchestrator.
//
// Pipeline:
//   1. Build query from top keywords (limited to keep URL length sane).
//   2. Query OpenAlex + PubMed in parallel.
//   3. Merge results into ReviewerCandidate[] (de-duped by orcid > openalex id > lowercased name).
//   4. Apply COI filter: drop candidates whose name or affiliation matches the
//      manuscript's own author list or institution list.
//   5. Return top N candidates.
//
// PRD §9.7 degraded-banner discipline:
//   - Both APIs fail → degraded:true, candidates:[]
//   - One API fails → partial:true, candidates from the surviving source

import { searchWorks } from './openalex';
import { searchPubmed } from './pubmed';
import type {
  OpenAlexAuthor,
  OpenAlexWork,
  PubMedRecord,
  ReviewerCandidate,
  ReviewerSearchResult,
} from './types';

export interface ReviewerSearchInput {
  /** Top-ranked keywords from the manuscript. We use up to 8. */
  keywords: string[];
  /** Surnames of the manuscript's authors (lowercased). */
  excludeAuthors: string[];
  /** Institution strings from the manuscript (lowercased substrings to match). */
  excludeAffiliations: string[];
  /** Optional mailto for polite-pool. */
  mailto?: string;
  /** Optional NCBI API key. */
  ncbiApiKey?: string;
  /** Cap on returned candidates; default 20. */
  limit?: number;
}

function buildQuery(keywords: string[]): string {
  return keywords.slice(0, 8).join(' ');
}

function candidateKey(c: { orcid: string | null; openalexId: string | null; name: string }): string {
  if (c.orcid) return `orcid:${c.orcid.toLowerCase()}`;
  if (c.openalexId) return `oa:${c.openalexId}`;
  return `name:${c.name.toLowerCase().replace(/\s+/g, ' ').trim()}`;
}

function authorMatchesExcluded(
  name: string,
  affiliation: string | null,
  excludeAuthors: string[],
  excludeAffiliations: string[],
): boolean {
  const lowerName = name.toLowerCase();
  for (const surname of excludeAuthors) {
    if (lowerName.includes(surname)) return true;
  }
  if (affiliation) {
    const lowerAff = affiliation.toLowerCase();
    for (const a of excludeAffiliations) {
      if (a && lowerAff.includes(a)) return true;
    }
  }
  return false;
}

function fromOpenAlexWork(work: OpenAlexWork, author: OpenAlexAuthor): ReviewerCandidate {
  return {
    key: candidateKey({ orcid: author.orcid, openalexId: author.id, name: author.displayName }),
    name: author.displayName,
    affiliation: author.affiliation,
    orcid: author.orcid,
    email: null,
    source: 'openalex',
    relevantPaperTitle: work.title,
    relevantPaperDoi: work.doi,
    relevantPaperYear: work.year,
  };
}

function fromPubMedRecord(record: PubMedRecord): ReviewerCandidate[] {
  return record.authors.slice(0, 3).map((author) => ({
    key: candidateKey({ orcid: null, openalexId: null, name: author.name }),
    name: author.name,
    affiliation: author.affiliation,
    orcid: null,
    email: null,
    source: 'pubmed',
    relevantPaperTitle: record.title,
    relevantPaperDoi: record.doi,
    relevantPaperYear: record.year,
  }));
}

export async function searchReviewers(input: ReviewerSearchInput): Promise<ReviewerSearchResult> {
  const query = buildQuery(input.keywords);
  const limit = input.limit ?? 20;

  if (query.trim().length === 0) {
    return { candidates: [], degraded: false, partial: false };
  }

  const [openalexRes, pubmedRes] = await Promise.all([
    searchWorks(query, { mailto: input.mailto }),
    searchPubmed(query, { apiKey: input.ncbiApiKey }),
  ]);

  const byKey = new Map<string, ReviewerCandidate>();

  if (openalexRes.ok) {
    // Take the *first* author of each work (most likely the corresponding researcher).
    for (const work of openalexRes.data) {
      const firstAuthor = work.authors[0];
      if (!firstAuthor) continue;
      if (
        authorMatchesExcluded(
          firstAuthor.displayName,
          firstAuthor.affiliation,
          input.excludeAuthors,
          input.excludeAffiliations,
        )
      ) {
        continue;
      }
      const cand = fromOpenAlexWork(work, firstAuthor);
      if (!byKey.has(cand.key)) byKey.set(cand.key, cand);
    }
  }

  if (pubmedRes.ok) {
    for (const record of pubmedRes.data) {
      for (const cand of fromPubMedRecord(record)) {
        if (
          authorMatchesExcluded(
            cand.name,
            cand.affiliation,
            input.excludeAuthors,
            input.excludeAffiliations,
          )
        ) {
          continue;
        }
        if (!byKey.has(cand.key)) byKey.set(cand.key, cand);
      }
    }
  }

  const both_failed = !openalexRes.ok && !pubmedRes.ok;
  const partial = (openalexRes.ok ? 0 : 1) + (pubmedRes.ok ? 0 : 1) === 1;

  return {
    candidates: [...byKey.values()].slice(0, limit),
    degraded: both_failed,
    partial,
  };
}
